import React, { useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  Flex,
  Input,
  Select,
  Spin,
  Tooltip,
  Typography,
} from 'antd'
import { ArrowLeftOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { FormattedMessage, injectIntl } from 'react-intl'
import { requireAuthentication } from '../helpers/authentication'
import { ensureFaultRow } from '../api/faults'
import { createProductionLineProblemLog } from '../api/controller-api'
import { useFaultByProblemLogId } from '../hooks/Faults'
import { useVehicleTestById } from '../hooks/VehicleTests'
import { REPAIR_NOTE_FAULT_KEY } from './constants'
import './styles.scss'

const { Text } = Typography
const { TextArea } = Input

// ─── Sub-components ───────────────────────────────────────────────────────────

function PageHeader({ onGoBack }) {
  return (
    <Flex align="center" gap="middle" className="repair-note-header">
      <Button icon={<ArrowLeftOutlined />} shape="round" onClick={onGoBack}>
        <FormattedMessage id="productionLineDashboard.repairNote.goBack" />
      </Button>
      <Text strong style={{ fontSize: 16 }}>
        <FormattedMessage id="productionLineDashboard.repairNote.title" />
      </Text>
    </Flex>
  )
}

function DtcCodesField({ dtcOptions, value, onChange, intl }) {
  return (
    <div>
      <div className="repair-note-field-label">
        <FormattedMessage id="productionLineDashboard.repairNote.dtcCodes" /> :
      </div>
      <Select
        mode="multiple"
        value={value}
        onChange={onChange}
        options={dtcOptions}
        placeholder={intl.formatMessage({
          id: 'productionLineDashboard.repairNote.dtcCodesPlaceholder',
        })}
        style={{ width: '100%' }}
      />
    </div>
  )
}

function NotesField({ value, onChange, intl }) {
  return (
    <div>
      <div className="repair-note-field-label">
        <Text type="danger">* </Text>
        <FormattedMessage id="productionLineDashboard.repairNote.notes" />{' '}
        <Tooltip
          title={intl.formatMessage({
            id: 'productionLineDashboard.repairNote.notesTooltip',
          })}
        >
          <QuestionCircleOutlined style={{ cursor: 'pointer' }} />
        </Tooltip>{' '}
        :
      </div>
      <TextArea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={intl.formatMessage({
          id: 'productionLineDashboard.repairNote.notesPlaceholder',
        })}
        rows={5}
      />
    </div>
  )
}

function RepairNoteForm({
  dtcOptions,
  initialNotes = '',
  initialDtcs = [],
  onSubmit,
  onCancel,
  isSubmitting,
  intl,
}) {
  const [selectedDtcs, setSelectedDtcs] = useState(initialDtcs)
  const [notes, setNotes] = useState(initialNotes)

  const canSubmit = notes.trim().length > 0

  return (
    <Card className="repair-note-form-card">
      <Flex vertical gap="large">
        <DtcCodesField
          dtcOptions={dtcOptions}
          value={selectedDtcs}
          onChange={setSelectedDtcs}
          intl={intl}
        />
        <NotesField value={notes} onChange={setNotes} intl={intl} />
        <Flex gap="small">
          <Button
            type="primary"
            onClick={() => onSubmit({ dtcs: selectedDtcs, notes })}
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
          >
            <FormattedMessage id="productionLineDashboard.repairNote.submit" />
          </Button>
          <Button onClick={onCancel} disabled={isSubmitting}>
            <FormattedMessage id="productionLineDashboard.repairNote.cancel" />
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function RepairNotePage({ intl }) {
  const history = useHistory()
  const location = useLocation()
  // The route's `sourceType` tells the two origins apart even before data
  // has loaded (see AssociateIssuePage.js for the same pattern).
  const { sourceType, sourceId } = useParams()
  const isVehicleTestOrigin = sourceType === 'vehicle-test'

  const vehicleTestFromState = location.state?.vehicleTest
  // `fault` is present when reached from the Incident List; `vehicleTest` is
  // present when reached from the Production Line Dashboard (either directly
  // from its results table, or via AssociateIssuePage's Add/Edit Note button)
  // -- same dual-origin pattern as AssociateIssuePage.js.
  const faultFromState = location.state?.fault

  // location.state is empty on a page refresh or a shared link -- fall back
  // to fetching by the route param in that case.
  const { vehicleTest: fetchedVehicleTest, isLoading: isLoadingVehicleTest } =
    useVehicleTestById(
      !vehicleTestFromState && isVehicleTestOrigin ? sourceId : null,
    )
  const { fault: fetchedFault, isLoading: isLoadingFault } =
    useFaultByProblemLogId(
      !faultFromState && !isVehicleTestOrigin ? sourceId : null,
    )

  const vehicleTest = vehicleTestFromState ?? fetchedVehicleTest ?? null
  const fault = faultFromState ?? fetchedFault ?? null
  const source = vehicleTest ?? fault
  const isResolvingSource = isVehicleTestOrigin
    ? !vehicleTestFromState && isLoadingVehicleTest
    : !faultFromState && isLoadingFault

  // location.state carries these explicitly when navigated to normally;
  // when falling back to a route-param fetch, derive them from the
  // re-fetched source's own repair_note_* fields instead.
  const existingProblemLogId =
    location.state?.existingProblemLogId ?? source?.repair_note_problem_log_id
  const existingAnnotation =
    location.state?.existingAnnotation ?? source?.repair_note_annotation ?? ''
  const existingDtcs =
    location.state?.existingDtcs ?? source?.repair_note_dtcs ?? []

  const isEditMode = existingProblemLogId != null

  const [isSubmitting, setIsSubmitting] = useState(false)

  // vehicleTest.dtcs is [{code, description}], fault.DTCs is [{dtc, acronym,
  // short_desc, ...}] -- both reduce to the same {value, label} shape the DTC
  // select needs. `value` stays the bare code (that's what gets submitted);
  // `label` appends the description/short_desc when one's available, purely
  // for display.
  const dtcOptions = vehicleTest
    ? (vehicleTest.dtcs ?? []).map((dtc) => ({
        value: dtc.code,
        label: dtc.description ? `${dtc.code} - ${dtc.description}` : dtc.code,
      }))
    : (fault?.DTCs ?? []).map((dtc) => ({
        value: dtc.dtc,
        label: dtc.short_desc ? `${dtc.dtc} - ${dtc.short_desc}` : dtc.dtc,
      }))

  const handleGoBack = () => history.goBack()

  const handleSubmit = async ({ dtcs, notes }) => {
    if (!source?.make_model_id) return
    setIsSubmitting(true)
    try {
      await ensureFaultRow({
        makeModelId: source.make_model_id,
        key: REPAIR_NOTE_FAULT_KEY,
      })
      const { id: created_id } = await createProductionLineProblemLog({
        id: existingProblemLogId,
        makeModelId: source.make_model_id,
        vehicleTestResultId: source.vehicle_test_result_id,
        vin: source.vin,
        reason: REPAIR_NOTE_FAULT_KEY,
        annotation: notes,
        dtcs,
      })
      // source was captured before this save (it's just router state from
      // whenever the user clicked "Associate"), so it still has the old/absent
      // repair_note_* fields -- merge in what we just saved rather than handing
      // the stale object back, or AssociateIssuePage renders "Add Note" with no
      // note text even though the save succeeded.
      const updatedRepairNoteFields = {
        repair_note_problem_log_id: isEditMode
          ? existingProblemLogId
          : created_id,
        repair_note_annotation: notes,
        repair_note_dtcs: dtcs,
      }
      if (vehicleTest) {
        history.push(`/production-line-dashboard/associate/${vehicleTest.id}`, {
          vehicleTest: { ...vehicleTest, ...updatedRepairNoteFields },
        })
      } else {
        history.push(`/incidents/associate/${fault.id}`, {
          fault: { ...fault, ...updatedRepairNoteFields },
        })
      }
    } catch (err) {
      console.error('Failed to submit repair note:', err)
      setIsSubmitting(false)
    }
  }

  if (isResolvingSource) {
    return (
      <div id="repair-note-page">
        <Flex justify="center" style={{ marginTop: 48 }}>
          <Spin />
        </Flex>
      </div>
    )
  }

  return (
    <div id="repair-note-page">
      <PageHeader onGoBack={handleGoBack} />
      <div className="repair-note-body">
        <RepairNoteForm
          dtcOptions={dtcOptions}
          initialNotes={existingAnnotation}
          initialDtcs={existingDtcs}
          onSubmit={handleSubmit}
          onCancel={handleGoBack}
          isSubmitting={isSubmitting}
          intl={intl}
        />
      </div>
    </div>
  )
}

export default requireAuthentication(injectIntl(RepairNotePage))
